import { create } from "zustand";
import { api, type MeResponse, type Organization } from "../api/client";
import {
  b64decode,
  b64encode,
  buildEnrollment,
  deriveMasterKey,
  rewrapUserKey,
  unlockKeys,
  unwrapOrgKey,
  unwrapUserKey,
  KDF_MEM_KIB,
  KDF_TIME,
  KDF_PAR,
  type UnlockedKeys,
} from "../crypto/vault";
import { initSession, isTauri, lockSession, offlineArm, offlineCheck } from "../desktop/bridge";
import { useOffline } from "./offline";

/**
 * Arm the desktop offline cache (§15) after unlock — best-effort, never blocks. On desktop with
 * offline enabled, re-derive the master key transiently from the password (not retained in state)
 * and hand it + the kdf salt to the native side, which builds the snapshot in the background.
 */
async function armOfflineCache(password: string, salt: Uint8Array): Promise<void> {
  if (!isTauri()) return;
  try {
    // Enforce server-side device revocation / remote-wipe (§15) before (re)building the snapshot: a
    // revoked or wipe-requested device must wipe its cache and NOT rebuild it. offlineCheck() does
    // the wipe natively; skip arming when it fires. (Requires the native session from initSession.)
    const status = await offlineCheck();
    if (status.revoked || status.wipe) return;
    const dev = await api.getSelfDevice();
    if (!dev.offline_enabled) return;
    await offlineArm(b64encode(deriveMasterKey(password, salt)), b64encode(salt));
  } catch {
    /* offline caching is best-effort; failures must not break unlock */
  }
}

// "offline-locked": server unreachable + a usable local cache exists → show the offline-unlock
// screen. "offline-unlocked": the snapshot is decrypted and rendered read-only (no online keys).
type Phase = "loading" | "enroll" | "locked" | "unlocked" | "offline-locked" | "offline-unlocked";

interface VaultState {
  phase: Phase;
  me: MeResponse | null;
  keys: UnlockedKeys | null;
  orgKeys: Record<string, Uint8Array>;
  error: string | null;
  busy: boolean;
  refresh: () => Promise<void>;
  enroll: (password: string) => Promise<void>;
  unlock: (password: string) => Promise<void>;
  /** Change the master password (§7.2): re-wrap the User key under the new password. Throws on a
   *  wrong current password or a failed save; the session stays unlocked (keys are unchanged). */
  changeMaster: (currentPassword: string, newPassword: string) => Promise<void>;
  lock: () => void;
  /** Switch to the offline-unlock screen (server unreachable, usable cache present). */
  enterOfflineLocked: () => void;
  /** Unlock the local snapshot read-only; resolves true on success. */
  offlineEnter: (password: string) => Promise<boolean>;
  orgKeyFor: (org: Organization) => Uint8Array;
  reset: () => void;
}

export const useVault = create<VaultState>((set, get) => ({
  phase: "loading",
  me: null,
  keys: null,
  orgKeys: {},
  error: null,
  busy: false,

  refresh: async () => {
    const me = await api.me();
    set({ me, phase: me.enrolled ? "locked" : "enroll", error: null });
  },

  enroll: async (password) => {
    set({ busy: true, error: null });
    try {
      const m = buildEnrollment(password);
      await api.enroll({
        public_key: b64encode(m.publicKey),
        wrapped_user_key: b64encode(m.wrappedUserKey),
        wrapped_private_key: b64encode(m.wrappedPrivateKey),
        kdf_salt: b64encode(m.salt),
        kdf_mem_kib: KDF_MEM_KIB,
        kdf_time: KDF_TIME,
        kdf_parallelism: KDF_PAR,
      });
      await get().refresh();
      await get().unlock(password);
    } catch (e) {
      set({ error: e instanceof Error ? e.message : "Enrollment failed" });
    } finally {
      set({ busy: false });
    }
  },

  unlock: async (password) => {
    set({ busy: true, error: null });
    try {
      const me = get().me;
      if (!me?.kdf || !me.wrapped_user_key || !me.wrapped_private_key) {
        throw new Error("missing key material");
      }
      const salt = b64decode(me.kdf.salt);
      const keys = unlockKeys(
        password,
        salt,
        b64decode(me.wrapped_user_key),
        b64decode(me.wrapped_private_key),
      );
      set({ keys, phase: "unlocked", orgKeys: {} });
      // Hand the private key to the native side, THEN (desktop only) enforce device revocation and
      // build the offline snapshot — both need the native session that initSession establishes.
      void initSession(keys)
        .then(() => armOfflineCache(password, salt))
        .catch(() => {});
    } catch {
      set({ error: "Wrong master password" });
    } finally {
      set({ busy: false });
    }
  },

  changeMaster: async (currentPassword, newPassword) => {
    const me = get().me;
    const keys = get().keys;
    if (!me?.kdf || !me.wrapped_user_key || !keys) throw new Error("Vault is locked");
    // Verify the current password up front (throws if the AEAD tag doesn't match) so we never
    // re-wrap onto an unverified identity.
    try {
      unwrapUserKey(currentPassword, b64decode(me.kdf.salt), b64decode(me.wrapped_user_key));
    } catch {
      throw new Error("Current master password is incorrect");
    }
    // Re-wrap the (unchanged) in-RAM User key under the new password; private/org/item keys untouched.
    const { wrappedUserKey, salt } = rewrapUserKey(keys.userKey, newPassword);
    await api.changeMaster({
      wrapped_user_key: b64encode(wrappedUserKey),
      kdf_salt: b64encode(salt),
      kdf_mem_kib: KDF_MEM_KIB,
      kdf_time: KDF_TIME,
      kdf_parallelism: KDF_PAR,
    });
    // Keep the in-session `me` consistent so a later lock→unlock uses the NEW salt/wrap.
    set({
      me: {
        ...me,
        wrapped_user_key: b64encode(wrappedUserKey),
        kdf: { salt: b64encode(salt), mem_kib: KDF_MEM_KIB, time: KDF_TIME, parallelism: KDF_PAR },
      },
    });
    // Re-seal the desktop offline cache under the new master key (best-effort, desktop only).
    void armOfflineCache(newPassword, salt);
  },

  lock: () => {
    void lockSession(); // zeroize the native session (desktop only)
    const offline = get().phase === "offline-unlocked" || get().phase === "offline-locked";
    if (offline) {
      // Offline: drop the plaintext snapshot and return to the offline-unlock screen (the online
      // unlock form can't work without server key material).
      useOffline.getState().exit();
      set({ keys: null, orgKeys: {}, phase: "offline-locked", error: null });
    } else {
      set({ keys: null, orgKeys: {}, phase: "locked", error: null });
    }
  },

  enterOfflineLocked: () => set({ phase: "offline-locked", error: null }),

  offlineEnter: async (password) => {
    const ok = await useOffline.getState().enter(password);
    if (ok) set({ phase: "offline-unlocked", error: null });
    return ok;
  },

  orgKeyFor: (org) => {
    const cached = get().orgKeys[org.id];
    if (cached) return cached;
    const keys = get().keys;
    if (!keys) throw new Error("vault locked");
    const orgKey = unwrapOrgKey(keys.privateKey, b64decode(org.wrapped_org_key));
    set((s) => ({ orgKeys: { ...s.orgKeys, [org.id]: orgKey } }));
    return orgKey;
  },

  reset: () => set({ phase: "loading", me: null, keys: null, orgKeys: {}, error: null }),
}));
